// Recipe schema helpers: empty/default shapes, import normalization, and
// the ingredient/instruction "component grouping" logic. Ported as-is from
// WEEKLY_2.HTM.
import { CATEGORIES, DIET_TAGS, MEAL_TYPES, NON_NUMERIC_UNITS, RECIPE_TYPES, SEASONS, UNITS, COOKING_METHODS } from "./constants";
import { uid } from "./utils";

export const emptyIngredient = () => ({ id: uid(), component: "", type: "ingredient", name: "", prep: "", quantity: "", unit: "", category: "other" });
export const emptyInstruction = () => ({ id: uid(), component: "", type: "step", text: "" });

export const emptyRecipe = () => ({
  id: null,
  title: "",
  cuisine: "",
  source: "",
  sourceUrl: "",
  favorite: false,
  mealTypes: [],
  recipeType: "entree",
  dietTags: [],
  seasons: [],
  cookingMethod: [],
  activeTime: 15,
  cookTime: 15,
  servings: 4,
  yield: "",
  caloriesPerServing: 0,
  proteinPerServing: 0,
  carbsPerServing: 0,
  fatPerServing: 0,
  isPublic: false,
  editableByOthers: false,
  ingredients: [emptyIngredient()],
  instructions: [emptyInstruction()],
});

// Groups a flat list of items (ingredients or instructions, both carrying an
// id + component field) into a render-ready sequence: a header entry is
// inserted whenever the component changes from the previous item, and
// consecutive same-component items are treated as one visual group. Items
// with no component (component === "") render with no header, as the
// implicit "main" list.
//
// Each item row also carries `firstOfRun`: true exactly when this item is
// either the very first row in the whole list, or the first row after a
// component change (i.e. immediately after a header would have been
// emitted). Consumers use this to avoid double-indenting a note that's the
// first thing in its run — see RecipeForm.jsx's note
// indentation logic.
export function buildGroupedRows(items) {
  const rows = [];
  let last = null;
  items.forEach((item) => {
    const firstOfRun = item.component !== last;
    if (item.component && firstOfRun) {
      rows.push({ kind: "header", key: `header-${item.id}`, label: item.component, itemId: item.id });
    }
    rows.push({ kind: "item", key: item.id, item, firstOfRun });
    last = item.component;
  });
  return rows;
}

// Grouping is based on exact string equality of `component`, so every new
// group needs a name distinct from whatever's already present — otherwise
// a second un-renamed "New component" click silently merges into the first
// group instead of starting a new one (no new header, since nothing changed).
export function nextComponentName(items) {
  const existing = new Set(items.map((i) => i.component).filter(Boolean));
  if (!existing.has("New component")) return "New component";
  let n = 2;
  while (existing.has(`New component ${n}`)) n++;
  return `New component ${n}`;
}

// Snaps any reasonably-shaped recipe object into the app's exact schema —
// used for importing recipes from a file. Missing or invalid fields fall
// back to sane defaults rather than failing the whole import. Accepts both
// the current schema and the pre-mealTypes/recipeType/dietTags shape, so
// older exports still import cleanly.
export function normalizeImportedRecipe(raw) {
  // activeTime/cookTime/servings are integer columns in Supabase (see
  // supabase/migrations/0002_relational_schema.sql) -- rounded, not just
  // coerced, so a fractional value like 15.3 doesn't import fine into JS
  // state only to fail with "invalid input syntax for type integer" later,
  // at save time.
  const int = (v, fallback = 0) => (isNaN(Number(v)) ? fallback : Math.round(Number(v)));
  // The per-serving nutrition fields are `numeric` columns -- real data
  // legitimately has decimals (12.5g protein), so these are only coerced,
  // never rounded.
  const num = (v, fallback = 0) => (isNaN(Number(v)) ? fallback : Number(v));
  const ingredients =
    Array.isArray(raw.ingredients) && raw.ingredients.length
      ? raw.ingredients
          .map((ing) => ({
            id: uid(),
            component: String(ing?.component || "").trim(),
            type: ing?.type === "note" ? "note" : "ingredient",
            name: String(ing?.name || "").trim(),
            prep: String(ing?.prep || ""),
            quantity: ing?.quantity != null && ing?.quantity !== "" ? String(ing.quantity) : "1",
            unit: UNITS.includes(ing?.unit) || NON_NUMERIC_UNITS.includes(ing?.unit) ? ing.unit : "",
            category: CATEGORIES.includes(ing?.category) ? ing.category : "other",
          }))
          .filter((ing) => ing.name)
      : [];
  const instructions =
    Array.isArray(raw.instructions) && raw.instructions.length
      ? raw.instructions
          .map((s) =>
            typeof s === "string"
              ? { id: uid(), component: "", type: "step", text: s.trim() }
              : { id: uid(), component: String(s?.component || "").trim(), type: s?.type === "note" ? "note" : "step", text: String(s?.text || "").trim() }
          )
          .filter((s) => s.text)
      : [];
  const mealTypes = Array.isArray(raw.mealTypes)
    ? raw.mealTypes.filter((m) => MEAL_TYPES.includes(m))
    : MEAL_TYPES.includes(raw.mealType)
    ? [raw.mealType]
    : [];
  const recipeType = RECIPE_TYPES.includes(raw.recipeType) ? raw.recipeType : RECIPE_TYPES.includes(raw.dishType) ? raw.dishType : "entree";
  const dietTags = Array.isArray(raw.dietTags)
    ? raw.dietTags.filter((t) => DIET_TAGS.includes(t))
    : [raw.vegetarian && "vegetarian", raw.vegan && "vegan", raw.glutenFree && "glutenFree"].filter(Boolean);
  return {
    id: null,
    title: String(raw.title || "").trim(),
    cuisine: String(raw.cuisine || "").trim(),
    source: String(raw.source || "").trim(),
    sourceUrl: String(raw.sourceUrl || "").trim(),
    favorite: !!raw.favorite,
    mealTypes,
    recipeType,
    dietTags,
    seasons: Array.isArray(raw.seasons) ? raw.seasons.filter((s) => SEASONS.includes(s)) : [],
    cookingMethod: Array.isArray(raw.cookingMethod) ? raw.cookingMethod.filter((m) => COOKING_METHODS.includes(m)) : [],
    activeTime: Math.max(0, int(raw.activeTime, 15)),
    cookTime: Math.max(0, int(raw.cookTime, 15)),
    servings: Math.max(1, int(raw.servings, 4)),
    yield: String(raw.yield || "").trim(),
    caloriesPerServing: Math.max(0, num(raw.caloriesPerServing, 0)),
    proteinPerServing: Math.max(0, num(raw.proteinPerServing, 0)),
    carbsPerServing: Math.max(0, num(raw.carbsPerServing, 0)),
    fatPerServing: Math.max(0, num(raw.fatPerServing, 0)),
    // Forced false regardless of what an imported file contains -- a bulk
    // import must not be able to make recipes public or eternally
    // shared-editable via a data field, only through the explicit
    // per-recipe UI checkbox at creation (db.js's importRecipes enforces
    // this too, as defense in depth).
    isPublic: false,
    editableByOthers: false,
    ingredients: ingredients.length ? ingredients : [emptyIngredient()],
    instructions: instructions.length ? instructions : [emptyInstruction()],
  };
}
